import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loadPdfDocument } from '../../utils/pdfjsWorker';

const PADDING = 2;

function clearMountNode(node) {
  if (!node) return;
  node.replaceChildren();
}

export default function PdfThumbnailPreview({
  pdfUrl,
  accentColor = '#4ce1b6',
  height,
  isDark = false,
  fill = false,
}) {
  const shellRef = useRef(null);
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(Boolean(pdfUrl));
  const [error, setError] = useState(null);

  useEffect(() => {
    const shellNode = shellRef.current;
    const mountNode = mountRef.current;

    if (!pdfUrl || !shellNode || !mountNode) {
      setLoading(Boolean(pdfUrl));
      setError(null);
      clearMountNode(mountNode);
      return undefined;
    }

    let cancelled = false;
    let resizeTimer;

    async function renderPreview() {
      clearMountNode(mountNode);
      setLoading(true);
      setError(null);

      const containerWidth = shellNode.clientWidth;
      const containerHeight = height || shellNode.clientHeight;
      // Wait for layout; ResizeObserver will retry.
      if (containerWidth < 8 || containerHeight < 8) {
        setLoading(true);
        return;
      }

      try {
        const pdf = await loadPdfDocument(pdfUrl);
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const availableWidth = Math.max(containerWidth - PADDING * 2, 40);
        const availableHeight = Math.max(containerHeight - PADDING * 2, 40);
        const scale = Math.min(availableWidth / viewport.width, availableHeight / viewport.height);
        const pixelRatio = window.devicePixelRatio || 1;
        const scaledViewport = page.getViewport({ scale: scale * pixelRatio });

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(scaledViewport.width);
        canvas.height = Math.floor(scaledViewport.height);
        canvas.style.display = 'block';
        canvas.style.width = `${Math.floor(scaledViewport.width / pixelRatio)}px`;
        canvas.style.height = `${Math.floor(scaledViewport.height / pixelRatio)}px`;
        canvas.style.maxWidth = '100%';
        canvas.style.maxHeight = '100%';
        if (fill) {
          canvas.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.12)';
          canvas.style.borderRadius = '2px';
        }

        const wrapper = document.createElement('div');
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.padding = `${PADDING}px`;
        wrapper.style.boxSizing = 'border-box';
        wrapper.appendChild(canvas);

        if (cancelled) return;
        mountNode.appendChild(wrapper);

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: scaledViewport,
        }).promise;

        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Preview failed');
          setLoading(false);
        }
      }
    }

    renderPreview();

    const observer = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!cancelled) renderPreview();
      }, 180);
    });
    observer.observe(shellNode);

    return () => {
      cancelled = true;
      window.clearTimeout(resizeTimer);
      observer.disconnect();
    };
  }, [pdfUrl, height, fill]);

  return (
    <Box
      ref={shellRef}
      sx={{
        position: fill ? 'absolute' : 'relative',
        inset: fill ? 0 : undefined,
        height: fill ? undefined : (height || 72),
        width: '100%',
        bgcolor: isDark ? '#0b1220' : '#fff',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box ref={mountRef} sx={{ width: '100%', height: '100%' }} />
      {loading && pdfUrl ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: 'none',
            bgcolor: isDark ? 'rgba(11, 18, 32, 0.55)' : 'rgba(255, 255, 255, 0.72)',
          }}
        >
          <CircularProgress size={22} sx={{ color: accentColor }} />
        </Box>
      ) : null}
      {error ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.75,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>
            Preview unavailable
          </Typography>
        </Box>
      ) : null}
      {!pdfUrl && !loading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            No preview
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
